import React, { useState } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faEnvelope, faPhone, faPaperPlane, faCircleCheck } from '@fortawesome/pro-regular-svg-icons';

import { submitContact } from '../../utils/inquiryService';
import { trackLead } from '../../utils/metaPixel';
import { isValidEmail, friendlyServerError } from '../../utils/formValidation';
import '../../pages/Summer/SummerCheckout.css';

// Custom CYV contact form (#23). Routes through the shared submitInquiry
// pipeline (inquiryKind:'contact') → leads collection + admin dashboard, and
// the server emails sing@ (forwards to Anthony + Grace). Gated exactly like the
// /register intake (App Check + INQUIRY_EMAILS_LIVE) — no new callable.
// Surface 9: restyled to the brand `.co-` form look (presentation only — fields,
// validation, honeypot, and the submitContact flow are unchanged).
const initialForm = {
	name: '',
	email: '',
	phone: '',
	message: '',
	companyWebsite: '', // honeypot — real users never see/fill this
};

const CyvContactForm = () => {
	const [form, setForm] = useState(initialForm);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState('');
	const [done, setDone] = useState(false);

	const update = (field) => (e) => {
		setForm((prev) => ({ ...prev, [field]: e.target.value }));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError('');
		if (!form.name.trim()) {
			setError('Please add your name so we know who we’re talking with.');
			return;
		}
		if (!isValidEmail(form.email)) {
			setError('Please add an email so we can write back — and double-check it looks right.');
			return;
		}
		if (!form.message.trim()) {
			setError('Please add a short message so we know how to help.');
			return;
		}
		setSubmitting(true);
		try {
			await submitContact({
				name: form.name.trim(),
				email: form.email.trim(),
				phone: form.phone.trim() || null,
				message: form.message.trim(),
				companyWebsite: form.companyWebsite, // honeypot
			});
			// Meta Pixel Lead — only on a confirmed-OK inquiry submit, never on a
			// validation/submit failure (those land in the catch below).
			trackLead();
			setDone(true);
			window.scrollTo({ top: 0, behavior: 'smooth' });
		} catch (err) {
			console.error('Contact submit failed:', err);
			setError(friendlyServerError(err, 'Something went wrong sending your message. Please try again.'));
			setSubmitting(false);
		}
	};

	if (done) {
		return (
			<div className='cyv co'>
				<div className='co-step'>
					<div className='co-notice'>
						<FontAwesomeIcon icon={faCircleCheck} />
						<span>
							Thanks for reaching out! We've got your message and will be in
							touch soon. You'll also get a confirmation by email.
						</span>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className='cyv co'>
			<form className='co-step' onSubmit={handleSubmit} noValidate>
				<div className='co-grid'>
					<div className='co-field'>
						<label htmlFor='contact-name'>
							<FontAwesomeIcon icon={faUser} /> Your name <span className='req'>*</span>
						</label>
						<input
							id='contact-name'
							className='co-input'
							type='text'
							required
							autoComplete='name'
							value={form.name}
							onChange={update('name')}
						/>
					</div>

					<div className='co-field'>
						<label htmlFor='contact-email'>
							<FontAwesomeIcon icon={faEnvelope} /> Email <span className='req'>*</span>
						</label>
						<input
							id='contact-email'
							className='co-input'
							type='email'
							required
							autoComplete='email'
							value={form.email}
							onChange={update('email')}
						/>
					</div>

					<div className='co-field full'>
						<label htmlFor='contact-phone'>
							<FontAwesomeIcon icon={faPhone} /> Phone{' '}
							<span className='optional'>(optional)</span>
						</label>
						<input
							id='contact-phone'
							className='co-input'
							type='tel'
							autoComplete='tel'
							value={form.phone}
							onChange={update('phone')}
						/>
					</div>

					<div className='co-field full'>
						<label htmlFor='contact-message'>
							How can we help? <span className='req'>*</span>
						</label>
						<textarea
							id='contact-message'
							className='co-input'
							rows={5}
							required
							value={form.message}
							onChange={update('message')}
						/>
					</div>
				</div>

				{/* Honeypot: visually hidden, off the tab order. A filled value = bot. */}
				<div aria-hidden='true' style={{ position: 'absolute', left: '-9999px', height: 0, overflow: 'hidden' }}>
					<label>
						Company website
						<input
							type='text'
							tabIndex={-1}
							autoComplete='off'
							value={form.companyWebsite}
							onChange={update('companyWebsite')}
						/>
					</label>
				</div>

				{error && (
					<Alert variant='danger' className='mt-3 mb-0'>
						{error}
					</Alert>
				)}

				<button type='submit' className='btn-cyv-teal co-pay' disabled={submitting}>
					{submitting ? (
						<>
							<Spinner animation='border' size='sm' className='me-2' />
							Sending…
						</>
					) : (
						<>
							<FontAwesomeIcon icon={faPaperPlane} className='me-2' />
							Send message
						</>
					)}
				</button>
				<p className='co-privacy-note'>
					By sending this, you agree to our{' '}
					<a href='/privacy' target='_blank' rel='noreferrer'>
						Privacy Policy
					</a>
					.
				</p>
			</form>
		</div>
	);
};

export default CyvContactForm;
